// Noodl SDK - webpack handles this require properly
const Noodl = require('@noodl/noodl-sdk');
const React = require('react');
import { App } from './App';
import { getStoreInstance } from './store/productEpdDataStore';
import { setNoodlContext } from '@/shared/api/noodlContext';

// Module-level reference for the node instance
let currentNodeInstance: NoodlNodeInstance | null = null;

interface NoodlNodeInstance {
  props: Record<string, unknown>;
  setOutputs: (outputs: Record<string, unknown>) => void;
  sendSignalOnOutput: (name: string) => void;
  flagOutputDirty: (name: string) => void;
  model: {
    inputs: Record<string, unknown>;
  };
}

/**
 * Set the current node instance reference
 */
export function setNodeInstance(instance: NoodlNodeInstance | null): void {
  currentNodeInstance = instance;
}

/**
 * Get the current node instance reference
 */
export function getNodeInstance(): NoodlNodeInstance | null {
  return currentNodeInstance;
}

/**
 * Product EPD Data Modal wrapper component that connects to Noodl
 */
function ProductEpdDataModalWrapper(_props: Record<string, unknown>) {
  React.useEffect(() => {
    // Set up Noodl outputs when component mounts
    const outputs = {
      onClose: () => {
        currentNodeInstance?.sendSignalOnOutput('OnClose');
      },
      onSave: () => {
        currentNodeInstance?.sendSignalOnOutput('OnSave');
      },
      onUpdate: (selectedData: any[], defaultSource: string) => {
        currentNodeInstance?.setOutputs({
          SelectedEpdData: selectedData,
          DefaultSource: defaultSource,
        });
        currentNodeInstance?.sendSignalOnOutput('OnUpdate');
      },
      onError: (errorMessage: string) => {
        currentNodeInstance?.setOutputs({ ErrorMessage: errorMessage });
        currentNodeInstance?.sendSignalOnOutput('OnError');
      },
      onRequestMissingEpd: (epdNameOrId: string) => {
        currentNodeInstance?.setOutputs({ RequestedEpdNameOrId: epdNameOrId });
        currentNodeInstance?.sendSignalOnOutput('OnRequestMissingEpd');
      },
    };

    // Store outputs for use by the App component
    (window as any).__productEpdDataModalOutputs = outputs;
  }, []);

  return <App />;
}

/**
 * Define the Product EPD Data Modal React node
 */
export const ProductEpdDataModalNode = Noodl.defineReactNode({
  name: 'ProductEpdDataModal',
  displayName: 'Product EPD Data Modal',
  category: 'Custom Modules',

  getReactComponent() {
    return ProductEpdDataModalWrapper;
  },

  initialize() {
    // Store reference to node instance when initialized
    setNodeInstance(this as unknown as NoodlNodeInstance);
    // Set Noodl context for API calls
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNoodlContext((this as any).context);

    // Initialize tracking values
    this._lastManufacturerId = '';
    this._lastSourceType = '';
    this._lastProductGroupId = null;
    this._lastSelectedEpdDataIds = [];
    this._lastTranslations = null;
    this._lastRequestMissingEpdTranslations = null;

    // Poll for input changes from connected inputs
    this._inputCheckInterval = setInterval(() => {
      const currentManufacturerId = this.inputs.ManufacturerId || '';
      const currentSourceType = this.inputs.SourceType || '';
      const currentProductGroupId = this.inputs.ProductGroupId || null;
      const currentSelectedEpdDataIds = this.inputs.SelectedEpdDataIds || [];
      const currentTranslations = this.inputs.Translations || null;
      const currentRequestMissingEpdTranslations = this.inputs.RequestMissingEpdTranslations || null;

      if (currentManufacturerId !== this._lastManufacturerId) {
        this._lastManufacturerId = currentManufacturerId;
        getStoreInstance().setManufacturerId(currentManufacturerId);
      }

      if (currentSourceType !== this._lastSourceType) {
        this._lastSourceType = currentSourceType;
        getStoreInstance().setSourceType(currentSourceType);
      }

      if (currentProductGroupId !== this._lastProductGroupId) {
        this._lastProductGroupId = currentProductGroupId;
        getStoreInstance().setProductGroupId(currentProductGroupId);
      }

      // Check if SelectedEpdDataIds changed (compare array by JSON)
      const currentIdsJson = JSON.stringify(currentSelectedEpdDataIds);
      const lastIdsJson = JSON.stringify(this._lastSelectedEpdDataIds);
      if (currentIdsJson !== lastIdsJson) {
        this._lastSelectedEpdDataIds = currentSelectedEpdDataIds;
        if (Array.isArray(currentSelectedEpdDataIds)) {
          getStoreInstance().setSelectedEpdDataIds(currentSelectedEpdDataIds);
        }
      }

      // Check if Translations changed (compare object by JSON)
      const currentTranslationsJson = JSON.stringify(currentTranslations);
      const lastTranslationsJson = JSON.stringify(this._lastTranslations);
      if (currentTranslationsJson !== lastTranslationsJson) {
        this._lastTranslations = currentTranslations;
        if (currentTranslations && typeof currentTranslations === 'object') {
          getStoreInstance().setTranslations(currentTranslations);
        }
      }

      // Check if RequestMissingEpdTranslations changed (compare object by JSON)
      const currentRequestMissingJson = JSON.stringify(currentRequestMissingEpdTranslations);
      const lastRequestMissingJson = JSON.stringify(this._lastRequestMissingEpdTranslations);
      if (currentRequestMissingJson !== lastRequestMissingJson) {
        this._lastRequestMissingEpdTranslations = currentRequestMissingEpdTranslations;
        if (currentRequestMissingEpdTranslations && typeof currentRequestMissingEpdTranslations === 'object') {
          getStoreInstance().setRequestMissingEpdTranslations(currentRequestMissingEpdTranslations);
        }
      }
    }, 50);
  },

  dispose() {
    // Clean up interval when node is removed
    if (this._inputCheckInterval) {
      clearInterval(this._inputCheckInterval);
    }
  },

  // Define Noodl data inputs
  inputs: {
    SourceType: {
      type: 'string',
      displayName: 'Source Type',
      group: 'Configuration',
      set(value: string) {
        getStoreInstance().setSourceType(value || '');
      },
    },
    ManufacturerId: {
      type: 'string',
      displayName: 'Manufacturer ID',
      group: 'Configuration',
      set(value: string) {
        getStoreInstance().setManufacturerId(value || '');
      },
    },
    ProductGroupId: {
      type: 'string',
      displayName: 'Product Group ID',
      group: 'Configuration',
      set(value: string) {
        getStoreInstance().setProductGroupId(value || null);
      },
    },
    SelectedEpdDataIds: {
      type: 'array',
      displayName: 'Selected EPD Data IDs',
      group: 'Data',
      default: [],
      set(value: any[]) {
        if (Array.isArray(value)) {
          getStoreInstance().setSelectedEpdDataIds(value);
        }
      },
    },
    Translations: {
      type: 'object',
      displayName: 'Translations',
      group: 'Translations',
      set(value: any) {
        if (value && typeof value === 'object') {
          getStoreInstance().setTranslations(value);
        }
      },
    },
    RequestMissingEpdTranslations: {
      type: 'object',
      displayName: 'Request Missing EPD Translations',
      group: 'Translations',
      set(value: any) {
        if (value && typeof value === 'object') {
          getStoreInstance().setRequestMissingEpdTranslations(value);
        }
      },
    },
  },

  // Define Noodl outputs
  outputs: {
    OnClose: {
      type: 'signal',
      displayName: 'On Close',
      group: 'Events',
    },
    OnSave: {
      type: 'signal',
      displayName: 'On Save',
      group: 'Events',
    },
    OnUpdate: {
      type: 'signal',
      displayName: 'On Update',
      group: 'Events',
    },
    OnError: {
      type: 'signal',
      displayName: 'On Error',
      group: 'Events',
    },
    OnRequestMissingEpd: {
      type: 'signal',
      displayName: 'On Request Missing EPD',
      group: 'Events',
    },
    SelectedEpdData: {
      type: 'array',
      displayName: 'Selected EPD Data',
      group: 'Data',
    },
    DefaultSource: {
      type: 'string',
      displayName: 'Default Source',
      group: 'Data',
    },
    RequestedEpdNameOrId: {
      type: 'string',
      displayName: 'Requested EPD Name or ID',
      group: 'Data',
    },
    ErrorMessage: {
      type: 'string',
      displayName: 'Error Message',
      group: 'Errors',
    },
  },

  inputProps: {},

  // Signal inputs
  signals: {
    OpenModal: {
      displayName: 'Open Modal',
      group: 'Actions',
      signal() {
        getStoreInstance().setModalOpen(true);
      },
    },
    CloseModal: {
      displayName: 'Close Modal',
      group: 'Actions',
      signal() {
        getStoreInstance().setModalOpen(false);
      },
    },
    FetchEpdData: {
      displayName: 'Fetch EPD Data',
      group: 'Actions',
      signal() {
        try {
          getStoreInstance().fetchEpdData();
        } catch (error) {
          console.error('[ProductEpdDataModalNode] Error calling fetchEpdData:', error);
        }
      },
    },
    Reset: {
      displayName: 'Reset',
      group: 'Actions',
      signal() {
        getStoreInstance().reset();
      },
    },
  },
});
